import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLazyGetFirmByIDQuery, useDeleteFirmByIDMutation } from "../../store/storageRoles/firmApi";
import { useGetTrustedDevicesQuery } from "../../store";
import useAuth from "../useAuth";
import usePermission from "../usePermission";
import { getTabsForFirm, getFirmIcon, getFirmTypeBadge, normalizeFirmTabId } from "../../utils/firmUtils";

export default function useFirmDetail() {
  const { firmId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const { isAdmin, hasPermission, hasAnyPermission } = usePermission();

  const [firm, setFirm] = useState(location.state?.firm || null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(normalizeFirmTabId(location.state?.toPageTab, { role: "storage" }) || "info");
  const [alerts, setAlerts] = useState([]);

  const [getFirmByID, { data: firmData, isLoading, isFetching }] =
    useLazyGetFirmByIDQuery();
  const [deleteFirmByID, { isLoading: deleteFirmLoading }] = useDeleteFirmByIDMutation();
  const { data: devicesData } = useGetTrustedDevicesQuery(undefined, { skip: !auth?.accessToken });
  const twoFactorEnabled = devicesData?.data?.twoFactorEnabled === true;

  const fetchFirm = useCallback(() => {
    if (auth?.accessToken && firmId) {
      getFirmByID({ token: auth.accessToken, id: firmId });
    }
  }, [auth?.accessToken, firmId, getFirmByID]);

  useEffect(() => {
    fetchFirm();
  }, [fetchFirm]);

  useEffect(() => {
    if (firmData?.data?.firm) {
      setFirm(firmData.data.firm);
    }
  }, [firmData]);

  const addAlert = (message, severity, autoRemoveMs = 3000) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, autoRemoveMs);
  };

  const dismissAlert = (id) => setAlerts((prev) => prev.filter((a) => a.id !== id));

  const handleFirmUpdate = (newFirm) => {
    setFirm(newFirm);
  };

  const handleGoBack = () => {
    if (location.state?.fromPage) {
      navigate(-1);
    } else {
      navigate("/Anasayfa/Firma");
    }
  };

  const handleDeleteFirm = async ({ password, passwordConfirm, code }) => {
    if (!firm?._id || !auth?.accessToken) return;
    try {
      await deleteFirmByID({
        id: firm._id,
        password,
        passwordConfirm,
        ...(twoFactorEnabled && code ? { code } : {}),
        token: auth.accessToken,
      }).unwrap();
      addAlert("Firma silindi.", "success");
      setDeleteModalOpen(false);
      navigate("/Anasayfa/Firma");
    } catch (err) {
      addAlert(err?.data?.message || "İşlem başarısız.", "error");
    }
  };

  const handleNavigateToFirm = (selectedFirm) => {
    navigate(`/Anasayfa/Firma/${selectedFirm._id}`, {
      state: { firm: selectedFirm, fromPage: "Firma" },
    });
  };

  const tabs = useMemo(() => {
    if (!firm) return [];
    return getTabsForFirm(firm).filter((tab) => {
      if (!tab.permission) return true;
      if (Array.isArray(tab.permission)) return hasAnyPermission(...tab.permission);
      return hasPermission(tab.permission);
    });
  }, [firm, hasPermission, hasAnyPermission]);

  // Firma veya tab listesi değiştiğinde, selectedTab izinli değilse ilk izinli sekmeye geç
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((t) => t.id === selectedTab)) {
      setSelectedTab(tabs[0].id);
    }
  }, [tabs, selectedTab]);

  const firmIcon = firm ? getFirmIcon(firm) : null;
  const firmTypeBadge = firm ? getFirmTypeBadge(firm) : null;

  return {
    firm,
    isAdmin,
    twoFactorEnabled,
    isFetching,
    isLoading,
    selectedTab,
    setSelectedTab,
    tabs,
    firmTypeBadge,
    firmIcon,
    alerts,
    addAlert,
    dismissAlert,
    handleFirmUpdate,
    handleGoBack,
    handleDeleteFirm,
    handleNavigateToFirm,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteFirmLoading,
  };
}
